import React, { useState, useEffect, useRef } from 'react';
import { Input, Button } from 'antd';
import { SearchOutlined, ArrowLeftOutlined, ArrowRightOutlined, InfoCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { TOURS, SECTIONS, StatusFlowchart } from '../help/help-content';
import AssistantAnswer from '../components/assistant/AssistantAnswer';
import { askAssistant, cookieFetch } from '../components/assistant/assistantApi';
import '../components/assistant/Assistant.css';
import './Help.css';

// Syntax: {helpId|display text}  or  {helpId|display text|hint on miss}
function parseBody(body) {
  const parts = [];
  const re = /\{([^|{}]+)\|([^|{}]+)(?:\|([^{}]*))?\}/g;
  let last = 0, m;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) parts.push({ type: 'text', content: body.slice(last, m.index) });
    parts.push({ type: 'ref', helpId: m[1].trim(), text: m[2].trim(), hint: m[3]?.trim() ?? null });
    last = re.lastIndex;
  }
  if (last < body.length) parts.push({ type: 'text', content: body.slice(last) });
  return parts;
}

const isButtonRef = (helpId) =>
  helpId.endsWith('-button') || helpId === 'generate-submit';

// Maps order status → { message, tourId } for context suggestion banner
const STATUS_SUGGESTION = {
  fresh:     { message: 'Next step: generate an STWXE to begin shipment prep.', tourId: 'stwxe-order' },
  stwxe:     { message: 'Next step: generate an Invoice to mark as shipped.',    tourId: 'invoice'     },
  shipped:   { message: 'Options: Consign for partial deliveries, or Mark Paid to close out.', tourId: 'consign' },
  consigned: { message: 'Consignment active: generate Invoices for each delivery batch.',      tourId: 'consign' },
  paid:      { message: 'Order is fully paid and closed.',                       tourId: null         },
};

const HelpPage = () => {
  // null = show flowchart landing; number = index into TOURS
  const [selectedTour, setSelectedTour] = useState(null);
  const [stepIndex, setStepIndex]       = useState(0);
  const [search, setSearch]             = useState('');
  const [hint, setHint]                 = useState(null);
  const [contextOrders, setContextOrders] = useState(null); // [{id, order_ID, status}]
  const [aiAnswer, setAiAnswer] = useState(null); // the assistant's answer to the search: {question, loading | error | id, text, covered}

  const channelRef   = useRef(null);
  const hintTimerRef = useRef(null);
  const currentStep  = useRef(null);

  const filteredTours = TOURS.filter(t =>
    search === '' ||
    t.title.toLowerCase().includes(search.toLowerCase()) ||
    t.steps.some(s =>
      s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.body.toLowerCase().includes(search.toLowerCase())
    )
  );

  const tour = selectedTour !== null ? (filteredTours[selectedTour] ?? null) : null;
  const step = tour?.steps[stepIndex] ?? null;
  currentStep.current = step;

  // Read ?tour=tourId&step=n from URL on mount for direct links from dialogs and the assistant
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tourParam = params.get('tour');
    if (tourParam) {
      const idx = TOURS.findIndex(t => t.id === tourParam);
      const step = Number(params.get('step')) || 1;
      if (idx !== -1) { setSelectedTour(idx); setStepIndex(Math.min(Math.max(step, 1), TOURS[idx].steps.length) - 1); }
    }
  }, []);

  useEffect(() => {
    channelRef.current = new BroadcastChannel('innocoso-help');

    channelRef.current.onmessage = (e) => {
      const { type, helpId, tourId, orders } = e.data ?? {};

      if (type === 'spotlight-miss') {
        const body  = currentStep.current?.body ?? '';
        const parts = parseBody(body);
        const ref   = parts.find(p => p.type === 'ref' && p.helpId === helpId);
        const msg   = ref?.hint ?? "That element isn't visible yet — open the relevant dialog first.";
        setHint(msg);
        clearTimeout(hintTimerRef.current);
        hintTimerRef.current = setTimeout(() => setHint(null), 5000);
        return;
      }

      if (type === 'context') {
        setContextOrders(orders?.length ? orders : null);
        return;
      }

      if (type === 'jump' && tourId) {
        const idx = TOURS.findIndex(t => t.id === tourId);
        if (idx !== -1) { setSelectedTour(idx); setStepIndex(0); setHint(null); }
        return;
      }
    };

    // Ask the main app for context on the currently selected order(s)
    setTimeout(() => channelRef.current?.postMessage({ type: 'request-context' }), 150);

    return () => { channelRef.current?.close(); clearTimeout(hintTimerRef.current); };
  }, []);

  const sendHighlight = (helpId) => {
    clearTimeout(hintTimerRef.current);
    setHint(null);
    channelRef.current?.postMessage({ type: 'spotlight', helpId });
  };

  const selectTour = (i) => { setSelectedTour(i); setStepIndex(0); setHint(null); };

  // Enter in the search box: the assistant answers the search as a question
  const askQuestion = async () => {
    const question = search.trim();
    if (question.length < 3) return;
    setAiAnswer({ question, loading: true });
    const result = await askAssistant(cookieFetch, {
      question,
      page: 'help window',
      selection: (contextOrders || []).map(o => ({ status: o.status })),
      history: [],
    });
    setAiAnswer(result.ok
      ? { question, id: result.data.id, text: result.data.answer, covered: result.data.covered }
      : { question, error: result.error });
  };

  // A help step linked in the assistant's answer
  const openStep = (tourId, step) => {
    const idx = TOURS.findIndex(t => t.id === tourId);
    if (idx === -1) return;
    setSearch('');
    setSelectedTour(idx);
    setStepIndex(Math.min(Math.max(step, 1), TOURS[idx].steps.length) - 1);
    setHint(null);
  };
  const showFlowchart = () => { setSelectedTour(null); setStepIndex(0); setHint(null); };

  const goPrev = () => {
    setHint(null);
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    } else if (selectedTour > 0) {
      const prev = selectedTour - 1;
      setSelectedTour(prev);
      setStepIndex(filteredTours[prev].steps.length - 1);
    }
  };

  const goNext = () => {
    setHint(null);
    if (stepIndex < (tour?.steps.length ?? 1) - 1) {
      setStepIndex(stepIndex + 1);
    } else if (selectedTour < filteredTours.length - 1) {
      setSelectedTour(selectedTour + 1);
      setStepIndex(0);
    }
  };

  const atStart = selectedTour === 0 && stepIndex === 0;
  const atEnd   = selectedTour === filteredTours.length - 1 &&
                  stepIndex === (tour?.steps.length ?? 1) - 1;

  const renderBody = (body) =>
    parseBody(body).map((part, i) => {
      if (part.type === 'text') return <span key={i}>{part.content}</span>;
      if (isButtonRef(part.helpId)) {
        return (
          <React.Fragment key={i}>
            <br />
            <span
              onClick={() => sendHighlight(part.helpId)}
              title="Click to highlight in app"
              style={{
                display: 'inline-block', padding: '3px 14px',
                background: '#1677ff', color: '#fff', borderRadius: 6,
                fontSize: 13, fontWeight: 500, cursor: 'pointer',
                userSelect: 'none', border: '1px solid #0958d9',
                margin: '4px 0', boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              }}
            >
              {part.text}
            </span>
            <br />
          </React.Fragment>
        );
      }
      return (
        <span
          key={i}
          className="help-ref"
          onClick={() => sendHighlight(part.helpId)}
          title="Click to highlight in app"
        >
          {part.text}
        </span>
      );
    });

  const renderSidebar = () => {
    if (search) {
      return filteredTours.map((t, i) => (
        <div key={t.id} className={`help-tour-item${i === selectedTour ? ' active' : ''}`} onClick={() => selectTour(i)}>
          {t.title}
        </div>
      ));
    }
    return SECTIONS.map(section => (
      <React.Fragment key={section.label}>
        <div className="help-section-header">{section.label}</div>
        {section.ids.map(id => {
          const idx = TOURS.findIndex(t => t.id === id);
          if (idx === -1) return null;
          return (
            <div
              key={id}
              className={`help-tour-item${idx === selectedTour ? ' active' : ''}`}
              onClick={() => selectTour(idx)}
            >
              {TOURS[idx].title}
            </div>
          );
        })}
      </React.Fragment>
    ));
  };

  // Context suggestion — derive from first selected order's status
  const suggestion = contextOrders?.length
    ? STATUS_SUGGESTION[contextOrders[0].status] ?? null
    : null;

  return (
    <div style={{
      display: 'flex', height: '100vh',
      fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      overflow: 'hidden',
    }}>
      {/* Sidebar */}
      <div style={{
        width: 240, background: '#1e1e2e', color: '#cdd6f4',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        borderRight: '1px solid #313244',
      }}>
        <div style={{ padding: '18px 16px 10px' }}>
          <div style={{ color: '#89b4fa', fontWeight: 700, fontSize: 15, letterSpacing: '-0.01em', marginBottom: 12 }}>
            Innocoso Help
          </div>
          <Input
            prefix={<SearchOutlined style={{ color: '#6c7086', fontSize: 12 }} />}
            placeholder="Search, or ask + Enter"
            value={search}
            onChange={e => { setSearch(e.target.value); setSelectedTour(search ? selectedTour : null); setStepIndex(0); setHint(null); }}
            onPressEnter={(e) => { if (!e.nativeEvent.isComposing) askQuestion(); }}
            title="Type words to search the topics, or a question and press Enter for the assistant (any language)"
            allowClear
            style={{ background: '#313244', border: 'none', color: '#cdd6f4', borderRadius: 6, fontSize: 13 }}
          />
        </div>

        {/* Overview / flowchart entry */}
        <div
          className={`help-tour-item${selectedTour === null && !search ? ' active' : ''}`}
          onClick={showFlowchart}
          style={{ borderBottom: '1px solid #313244', marginBottom: 4 }}
        >
          Order Lifecycle
        </div>

        <div style={{ overflowY: 'auto', flex: 1, paddingBottom: 16 }}>
          {renderSidebar()}
          {search && filteredTours.length === 0 && (
            <div style={{ padding: '12px 20px', color: '#6c7086', fontSize: 12 }}>No results</div>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        padding: '32px 40px 28px', overflowY: 'auto', background: '#fff',
      }}>
        {/* Context suggestion banner */}
        {contextOrders?.length > 0 && (
          <div style={{
            marginBottom: 22, padding: '10px 16px',
            background: 'rgba(137,180,250,0.07)',
            border: '1px solid rgba(137,180,250,0.25)',
            borderRadius: 8, display: 'flex', alignItems: 'center',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#89b4fa', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 3 }}>
                {contextOrders.length === 1
                  ? `Order ${contextOrders[0].order_ID} · ${contextOrders[0].status}`
                  : `${contextOrders.length} orders selected`}
              </div>
              {suggestion && (
                <div style={{ fontSize: 13, color: '#374151' }}>{suggestion.message}</div>
              )}
            </div>
            {suggestion?.tourId && (
              <button
                onClick={() => {
                  const idx = TOURS.findIndex(t => t.id === suggestion.tourId);
                  if (idx !== -1) selectTour(idx);
                }}
                style={{
                  padding: '5px 14px', background: '#1677ff', color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                }}
              >
                Open guide →
              </button>
            )}
          </div>
        )}

        {/* The assistant's answer to the search */}
        {aiAnswer && (
          <div className="as-help-card">
            <div className="as-help-q">
              <span><span className="as-help-label">Assistant</span>{aiAnswer.question}</span>
              <CloseOutlined onClick={() => setAiAnswer(null)} style={{ cursor: 'pointer', color: '#9ca3af' }} title="Close" />
            </div>
            {aiAnswer.loading && <div className="as-busy"><span /><span /><span /></div>}
            {aiAnswer.error && <div className="as-error">{aiAnswer.error}</div>}
            {aiAnswer.id && (
              <AssistantAnswer key={aiAnswer.id} id={aiAnswer.id} text={aiAnswer.text} covered={aiAnswer.covered}
                fetchFn={cookieFetch} onRef={sendHighlight} onStep={openStep} />
            )}
          </div>
        )}

        {/* Flowchart landing */}
        {selectedTour === null && !search && (
          <StatusFlowchart onJumpToTour={(tourId) => {
            const idx = TOURS.findIndex(t => t.id === tourId);
            if (idx !== -1) selectTour(idx);
          }} />
        )}

        {/* Tour step content */}
        {tour && step && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#89b4fa', marginBottom: 10 }}>
              {tour.title}
            </div>

            {/* Step progress bar */}
            {tour.steps.length > 1 && (
              <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
                {tour.steps.map((_, i) => (
                  <div
                    key={i}
                    onClick={() => { setStepIndex(i); setHint(null); }}
                    style={{
                      height: 3, flex: 1, borderRadius: 2,
                      background: i <= stepIndex ? '#89b4fa' : '#e5e7eb',
                      opacity: i < stepIndex ? 0.4 : 1,
                      cursor: 'pointer', transition: 'background 0.2s, opacity 0.2s',
                    }}
                  />
                ))}
              </div>
            )}

            <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: '#111', lineHeight: 1.25, letterSpacing: '-0.02em' }}>
              {step.title}
            </h2>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 18 }}>
              Step {stepIndex + 1} of {tour.steps.length}
            </div>

            <p style={{ fontSize: 14.5, lineHeight: 1.8, maxWidth: 560, color: '#374151', margin: 0, whiteSpace: 'pre-line' }}>
              {renderBody(step.body)}
            </p>

            {step.code && (
              <pre style={{
                marginTop: 16, padding: '12px 16px',
                background: '#1e1e2e', color: '#cdd6f4', borderRadius: 8,
                fontSize: 12.5, lineHeight: 1.7, maxWidth: 560,
                overflowX: 'auto',
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                whiteSpace: 'pre',
              }}>{step.code}</pre>
            )}

            {step.figure && React.createElement(step.figure)}

            {hint && (
              <div style={{
                marginTop: 16, padding: '9px 14px',
                background: 'rgba(203,166,247,0.08)', border: '1px solid rgba(203,166,247,0.3)',
                borderRadius: 7, color: '#9a7fc7', fontSize: 13, maxWidth: 520,
                display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
                <InfoCircleOutlined style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{hint}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 36 }}>
              <Button icon={<ArrowLeftOutlined />} onClick={goPrev} disabled={atStart} style={{ borderRadius: 6 }}>
                Previous
              </Button>
              <Button
                type={atEnd ? 'default' : 'primary'}
                onClick={goNext}
                disabled={atEnd}
                style={{ borderRadius: 6 }}
              >
                Next <ArrowRightOutlined />
              </Button>
            </div>
          </>
        )}

        {/* Search with no results */}
        {search && filteredTours.length === 0 && (
          <p style={{ color: '#9ca3af', marginTop: 40 }}>No topic contains "{search}". Press Enter to ask the assistant instead.</p>
        )}
      </div>
    </div>
  );
};

export default HelpPage;
